import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "w-full max-w-xl p-8 flex flex-col space-y-4" }
const _hoisted_2 = { class: "flex flex-row items-start space-x-2" }
const _hoisted_3 = { class: "flex -ml-3" }
const _hoisted_4 = { class: "text-xl font-semibold text-gray-900 self-center" }
const _hoisted_5 = { class: "text-base font-regular leading-normal text-gray-600 pb-2" }
const _hoisted_6 = { class: "w-full justify-start flex flex-col space-y-4" }
const _hoisted_7 = {
  key: 1,
  class: "flex flex-row w-full justify-between space-x-4"
}
const _hoisted_8 = {
  key: 4,
  class: "flex flex-end justify-end space-x-4"
}

import { computed, inject, onMounted, PropType, ref } from 'vue';
import axios, { CancelTokenSource } from 'axios';
import omitBy from 'lodash-es/omitBy';
import isNil from 'lodash-es/isNil';
import { BaseButton } from '@/lib/components/Button';
import { BaseIcon } from '@/lib/components/Icon';
import { BaseSelect } from '@/lib/components/Select';
import { BaseCheckbox } from '@/lib/components/Checkbox';
import { BaseDatePicker } from '@/lib/components/Date';
import { ITranslatedOption, IValidationError } from '@/lib';
import {
  CopdConsultType,
  CopdMeasurementReviewParams,
  CopdOutcome,
  CopdProgramReviewConsult,
  CopdReason,
  Patient,
  Worklist
} from '@/models';
import { CopdProgramService } from '@/services/api';
import {
  changeHomeMonitoringPlanReasons,
  consultTypes,
  dischargeReasonsCopdAa,
  reviewOutcomes,
  selectableDischargeReasons
} from '@/helpers/home-monitoring.helper';
import dayjs from 'dayjs';
import { isFeatureFlagEnabled } from '@/helpers/feature-flag.helper';
import { FEATURES } from '@/constants';
import { HomeMonitoringPathways } from '@/lib/components/Programs/IbdProgram/constants';


export default /*#__PURE__*/_defineComponent({
  __name: 'ReviewMeasurementsModal',
  props: {
  programId: {
    type: String,
    required: true
  },
  status: {
    type: String,
    required: true
  },
  toBeReviewedList: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  nextConsult: {
    type: Object as PropType<CopdProgramReviewConsult | null>,
    default: null
  },
  worklist: {
    type: Object as PropType<Worklist>,
    default: () => null
  },
  patient: {
    type: Object as PropType<Patient>,
    default: () => null
  }
},
  emits: ["close", "record-event", "discharged"],
  setup(__props, { emit: __emit }) {

const props = __props;
const emit = __emit;

const updatePatient = inject('updatePatient', null);
const request = ref<CancelTokenSource | null>(null);
const alreadyReviewed = ref(false);
const reviewParams = ref<CopdMeasurementReviewParams>({});
const patientScheduledExternally = ref(false);
const errors = ref<IValidationError>({
  errors: {},
  message: ''
});
const isCopdAdmissionAvoidanceFeatureEnabled = ref<boolean>(isFeatureFlagEnabled(FEATURES.COPD_ADMISSION_AVOIDANCE));
const copdProgramService = new CopdProgramService();

const today = computed(() => dayjs().format('YYYY-MM-DD'));
const isDischargeReview = computed(() => reviewParams.value.outcome === CopdOutcome.discharge);
const consultTypeList = computed(() => consultTypes);
const outcomeList = computed(() => reviewOutcomes);
const reasonOptions = computed<ITranslatedOption[]>(() => {
  if (reviewParams.value.outcome === CopdOutcome.discharge) {
    if (isCopdAdmissionAvoidanceFeatureEnabled.value && props.worklist?.slug === HomeMonitoringPathways.CopdAdmissionAvoidance) {
      return dischargeReasonsCopdAa;
    }
    return selectableDischargeReasons();
  }
  return changeHomeMonitoringPlanReasons;
});
const ensureNextConsultAtIsOnOrAfterToday = computed(() => (
  reviewParams.value.next_consult_at &&
  (reviewParams.value.next_consult_at >= today.value)
));
const submitDisabled = computed<boolean>(() => {
  if (isDischargeReview.value) {
    return false;
  }
  if (patientScheduledExternally.value) {
    return !ensureNextConsultAtIsOnOrAfterToday.value;
  }
  return true;
});

onMounted(() => {
  if (
    props.nextConsult &&
    (dayjs().isBefore(dayjs(props.nextConsult.consult_at)) || dayjs(props.nextConsult.consult_at).isToday())
  ) {
    reviewParams.value = {
      next_consult_at: props.nextConsult.consult_at,
      next_consult_type: props.nextConsult.consult_type
    };
  }

  emit('record-event', 'started review of program measurement');
});
const updateReviewParams = (newParams: CopdMeasurementReviewParams) => {
  reviewParams.value = omitBy({ ...reviewParams.value, ...newParams }, isNil);

  // clear errors on updated params
  Object.keys(newParams).forEach((updatedField) => {
    errors.value?.errors && delete errors.value.errors[updatedField];
  });

  // clear the message in case there are no errors
  if (!Object.keys(errors.value.errors).length) {
    errors.value.message = '';
  }
};
const closeReviewModal = () => {
  reviewParams.value = {};
  emit('close', true);
};
const reviewMeasurements = async () => {
  try {
    reviewParams.value = {
      ...reviewParams.value,
      measurements: props.toBeReviewedList
    };

    request.value = axios.CancelToken.source();
    await copdProgramService.reviewMeasurements(props.programId as string, reviewParams.value, {
      cancelToken: request.value.token
    });

    if (isCopdAdmissionAvoidanceFeatureEnabled.value && reviewParams.value.outcome === CopdOutcome.discharge) {
      const patient = { ...props.patient };
      if (patient?.copd_home_monitoring_latest_programs_across_all_pathways) {
        for (const program of patient.copd_home_monitoring_latest_programs_across_all_pathways) {
          if (program.id === props.programId) {
            program.discharged_program_review_id = props.programId;
            break;
          }
        }
      }
      updatePatient(patient);
    }

    emit('discharged');
    emit('record-event', 'completed review of program measurement');
    emit('close', true);
  } catch (e) {
    if (!axios.isCancel(e)) {
      if (!axios.isAxiosError(e)) {
        throw e;
      }
      errors.value = e.response.data;
      if (e.response.status === 400) {
        errors.value = { message: e.response.data.message, errors: {} };
        alreadyReviewed.value = true;
      } else {
        errors.value = e.response.data;
      }
    }
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(BaseIcon), {
          name: "outcome",
          stroke: false,
          height: "2.5rem"
        })
      ]),
      _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.$t('custom.uhb.copd.complete-review')), 1)
    ]),
    _createElementVNode("div", _hoisted_5, _toDisplayString(_ctx.$t('custom.uhb.copd.complete-review-desc')), 1),
    _createElementVNode("div", _hoisted_6, [
      _createVNode(_unref(BaseSelect), {
        "model-value": reviewParams.value?.outcome || '',
        error: errors.value?.errors?.outcome?.[0],
        label: _ctx.$t('custom.uhb.copd.outcome'),
        options: outcomeList.value,
        placeholder: _ctx.$t('custom.uhb.copd.select-outcome'),
        "data-dusk": "review-measurements-select-outcome",
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (updateReviewParams({ outcome: $event as CopdOutcome, reason: null })))
      }, null, 8, ["model-value", "error", "label", "options", "placeholder"]),
      (reviewParams.value?.outcome === 'change' || isDischargeReview.value)
        ? (_openBlock(), _createBlock(_unref(BaseSelect), {
            key: 0,
            "model-value": reviewParams.value.reason || '',
            error: errors.value?.errors?.reason?.[0],
            label: _ctx.$t('custom.uhb.copd.outcome-reason'),
            options: reasonOptions.value,
            placeholder: _ctx.$t('custom.uhb.copd.select-reason'),
            "data-dusk": "review-measurements-select-outcome-reason",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (updateReviewParams({ reason: $event as CopdReason })))
          }, null, 8, ["model-value", "error", "label", "options", "placeholder"]))
        : _createCommentVNode("", true),
      (reviewParams.value?.outcome && !isDischargeReview.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createVNode(_unref(BaseDatePicker), {
              modelValue: reviewParams.value.next_consult_at,
              "onUpdate:modelValue": [
                _cache[2] || (_cache[2] = ($event: any) => ((reviewParams.value.next_consult_at) = $event)),
                _cache[3] || (_cache[3] = ($event: any) => (updateReviewParams({ next_consult_at: $event })))
              ],
              class: "flex flex-col w-1/2 items-start",
              error: errors.value?.errors?.next_consult_at?.[0],
              label: _ctx.$t('custom.uhb.copd.next-consult-at'),
              "min-date": today.value,
              placeholder: _ctx.$t('custom.uhb.copd.select-next-consult-at'),
              "data-dusk": "review-measurements-next-consult-date-picker"
            }, null, 8, ["modelValue", "error", "label", "min-date", "placeholder"]),
            _createVNode(_unref(BaseSelect), {
              "model-value": reviewParams.value.next_consult_type,
              class: "flex w-1/2 h-full",
              error: errors.value?.errors?.next_consult_type?.[0],
              label: _ctx.$t('custom.uhb.copd.next-consult-type'),
              options: consultTypeList.value,
              placeholder: _ctx.$t('custom.uhb.copd.select-next-consult-type'),
              "data-dusk": "review-measurements-select-consult-type",
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (updateReviewParams({ next_consult_type: $event as CopdConsultType })))
            }, null, 8, ["model-value", "error", "label", "options", "placeholder"])
          ]))
        : _createCommentVNode("", true),
      (reviewParams.value && reviewParams.value.outcome && !isDischargeReview.value)
        ? (_openBlock(), _createBlock(_unref(BaseCheckbox), {
            key: 2,
            modelValue: patientScheduledExternally.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((patientScheduledExternally).value = $event)),
            label: _ctx.$t('custom.uhb.copd.patient-scheduled-externally'),
            shadow: true,
            border: true,
            "data-dusk": "review-measurements-patient-scheduled-externally"
          }, null, 8, ["modelValue", "label"]))
        : _createCommentVNode("", true),
      (alreadyReviewed.value)
        ? (_openBlock(), _createBlock(_unref(BaseButton), {
            key: 3,
            size: "small",
            color: "ghost",
            onClick: _cache[6] || (_cache[6] = ($event: any) => (_ctx.$emit('close', true)))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('platform.common.close')), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (!alreadyReviewed.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(_unref(BaseButton), {
              class: "mt-8",
              size: "large",
              color: "ghost",
              onClick: closeReviewModal
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('platform.common.cancel')), 1)
              ]),
              _: 1
            }),
            _createVNode(_unref(BaseButton), {
              class: "mt-8",
              size: "large",
              color: isDischargeReview.value ? 'alert' : 'primary',
              disabled: submitDisabled.value,
              "data-dusk": "update-review-button",
              onClick: reviewMeasurements
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(isDischargeReview.value ? _ctx.$t('custom.uhb.copd.discharge-patient') : _ctx.$t('platform.common.update')), 1)
              ]),
              _: 1
            }, 8, ["color", "disabled"])
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})